package com.example.monetix.db.composeApp

import app.cash.sqldelight.SuspendingTransacterImpl
import app.cash.sqldelight.db.AfterVersion
import app.cash.sqldelight.db.QueryResult
import app.cash.sqldelight.db.SqlDriver
import app.cash.sqldelight.db.SqlSchema
import com.example.monetix.db.AccountQueries
import com.example.monetix.db.BrandQueries
import com.example.monetix.db.BudgetQueries
import com.example.monetix.db.CategoryQueries
import com.example.monetix.db.FavoriteTemplateQueries
import com.example.monetix.db.FinancingQueries
import com.example.monetix.db.MerchantLocationQueries
import com.example.monetix.db.MonetixDatabase
import com.example.monetix.db.PiggyBankQueries
import com.example.monetix.db.RecurringExpenseQueries
import com.example.monetix.db.SavingsGoalQueries
import com.example.monetix.db.SettingsQueries
import com.example.monetix.db.TransactionQueries
import kotlin.Long
import kotlin.Unit
import kotlin.reflect.KClass

internal val KClass<MonetixDatabase>.schema: SqlSchema<QueryResult.AsyncValue<Unit>>
  get() = MonetixDatabaseImpl.Schema

internal fun KClass<MonetixDatabase>.newInstance(driver: SqlDriver): MonetixDatabase =
    MonetixDatabaseImpl(driver)

private class MonetixDatabaseImpl(
  driver: SqlDriver,
) : SuspendingTransacterImpl(driver), MonetixDatabase {
  override val accountQueries: AccountQueries = AccountQueries(driver)

  override val brandQueries: BrandQueries = BrandQueries(driver)

  override val budgetQueries: BudgetQueries = BudgetQueries(driver)

  override val categoryQueries: CategoryQueries = CategoryQueries(driver)

  override val favoriteTemplateQueries: FavoriteTemplateQueries = FavoriteTemplateQueries(driver)

  override val financingQueries: FinancingQueries = FinancingQueries(driver)

  override val merchantLocationQueries: MerchantLocationQueries = MerchantLocationQueries(driver)

  override val piggyBankQueries: PiggyBankQueries = PiggyBankQueries(driver)

  override val recurringExpenseQueries: RecurringExpenseQueries = RecurringExpenseQueries(driver)

  override val savingsGoalQueries: SavingsGoalQueries = SavingsGoalQueries(driver)

  override val settingsQueries: SettingsQueries = SettingsQueries(driver)

  override val transactionQueries: TransactionQueries = TransactionQueries(driver)

  public object Schema : SqlSchema<QueryResult.AsyncValue<Unit>> {
    override val version: Long
      get() = 2

    override fun create(driver: SqlDriver): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      driver.execute(null, """
          |CREATE TABLE AccountEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    name TEXT NOT NULL,
          |    logoEmoji TEXT,
          |    logoUri TEXT,
          |    isVisible INTEGER NOT NULL DEFAULT 1,
          |    isHidden INTEGER NOT NULL DEFAULT 0,
          |    isSavings INTEGER NOT NULL DEFAULT 0,
          |    isArchived INTEGER NOT NULL DEFAULT 0,
          |    isDeleted INTEGER NOT NULL DEFAULT 0,
          |    createdAtEpochMs INTEGER NOT NULL,
          |    updatedAtEpochMs INTEGER NOT NULL
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE BrandEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    name TEXT NOT NULL,
          |    logoUri TEXT
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE BudgetEntity (
          |    categoryId TEXT NOT NULL PRIMARY KEY,
          |    limitCents INTEGER NOT NULL,
          |    isDeleted INTEGER NOT NULL DEFAULT 0,
          |    createdAtEpochMs INTEGER NOT NULL,
          |    updatedAtEpochMs INTEGER NOT NULL
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE CategoryEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    name TEXT NOT NULL,
          |    isDeleted INTEGER NOT NULL DEFAULT 0,
          |    createdAtEpochMs INTEGER NOT NULL,
          |    updatedAtEpochMs INTEGER NOT NULL
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE FavoriteTemplateEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    description TEXT,
          |    amountCents INTEGER NOT NULL,
          |    categoryId TEXT NOT NULL,
          |    logoEmoji TEXT,
          |    logoUri TEXT,
          |    isDeleted INTEGER NOT NULL DEFAULT 0,
          |    createdAtEpochMs INTEGER NOT NULL,
          |    updatedAtEpochMs INTEGER NOT NULL
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE FinancingEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    name TEXT NOT NULL,
          |    creditorName TEXT,
          |    purpose TEXT,
          |    installmentAmountCents INTEGER NOT NULL,
          |    commissionCents INTEGER NOT NULL DEFAULT 0,
          |    extraInstallmentCents INTEGER,
          |    extraInstallmentMonth INTEGER,
          |    totalAmountCents INTEGER NOT NULL,
          |    remainingAmountCents INTEGER NOT NULL DEFAULT 0,
          |    accountId TEXT,
          |    categoryId TEXT,
          |    startEpochDay INTEGER NOT NULL,
          |    endEpochDay INTEGER NOT NULL,
          |    autoGenerateOnReminder INTEGER NOT NULL DEFAULT 1,
          |    isClosed INTEGER NOT NULL DEFAULT 0,
          |    isDeleted INTEGER NOT NULL DEFAULT 0,
          |    logoEmoji TEXT,
          |    logoUri TEXT,
          |    createdAtEpochMs INTEGER NOT NULL,
          |    updatedAtEpochMs INTEGER NOT NULL
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE MerchantLocationEntity (
          |    merchantKey TEXT NOT NULL PRIMARY KEY,
          |    categoryId TEXT NOT NULL,
          |    subcategoryText TEXT,
          |    latitude REAL NOT NULL,
          |    longitude REAL NOT NULL,
          |    visitCount INTEGER NOT NULL DEFAULT 1,
          |    lastVisitedEpochMs INTEGER NOT NULL
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE PiggyBankEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    name TEXT NOT NULL,
          |    accumulatedCents INTEGER NOT NULL,
          |    emoji TEXT NOT NULL,
          |    colorHex TEXT NOT NULL,
          |    imageUri TEXT,
          |    isDeleted INTEGER NOT NULL DEFAULT 0,
          |    createdAtEpochMs INTEGER NOT NULL,
          |    updatedAtEpochMs INTEGER NOT NULL
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE RecurringExpenseEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    name TEXT NOT NULL,
          |    type TEXT NOT NULL, -- 🚀 ECCO IL CAMPO FONDAMENTALE PER DISTINGUERE INCOME, EXPENSE e TRANSFER
          |    amountCents INTEGER NOT NULL,
          |    accountId TEXT NOT NULL,
          |    toAccountId TEXT,
          |    categoryId TEXT NOT NULL,
          |    dayOfMonth INTEGER NOT NULL,
          |    isEnabled INTEGER NOT NULL DEFAULT 1,
          |    isDeleted INTEGER NOT NULL DEFAULT 0,
          |    cycleTarget INTEGER DEFAULT 0,
          |    logoEmoji TEXT,
          |    logoUri TEXT,
          |    notes TEXT,
          |
          |    -- I CAMPI CHE ABBIAMO RECUPERATO DAL VECCHIO PROGETTO:
          |    autoGenerateOnReminder INTEGER NOT NULL DEFAULT 0,
          |    nextEpochDay INTEGER NOT NULL,
          |    frequency TEXT,
          |    isPaused INTEGER NOT NULL DEFAULT 0,
          |    startEpochDay INTEGER NOT NULL,
          |
          |    createdAtEpochMs INTEGER NOT NULL,
          |    updatedAtEpochMs INTEGER NOT NULL
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE SavingsGoalEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    name TEXT NOT NULL,
          |    targetAmountCents INTEGER NOT NULL,
          |    savedAmountCents INTEGER NOT NULL DEFAULT 0,
          |    emoji TEXT,
          |    imageUri TEXT,
          |    isCompleted INTEGER NOT NULL DEFAULT 0,
          |    isDeleted INTEGER NOT NULL DEFAULT 0,
          |    createdAtEpochMs INTEGER NOT NULL,
          |    updatedAtEpochMs INTEGER NOT NULL
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE SettingsEntity (
          |    key TEXT NOT NULL PRIMARY KEY,
          |    value TEXT NOT NULL
          |)
          """.trimMargin(), 0).await()
      driver.execute(null, """
          |CREATE TABLE TransactionEntity (
          |    id TEXT NOT NULL PRIMARY KEY,
          |    accountId TEXT,
          |    toAccountId TEXT,
          |    fromAccountId TEXT,
          |    amountCents INTEGER NOT NULL,
          |    type TEXT NOT NULL, -- 'INCOME', 'EXPENSE', 'TRANSFER'
          |    dateEpochDay INTEGER NOT NULL,
          |    categoryId TEXT NOT NULL,
          |    description TEXT,
          |    merchantKey TEXT,
          |    subcategoryText TEXT,
          |    logoEmoji TEXT,
          |    logoUri TEXT,
          |    isHidden INTEGER NOT NULL DEFAULT 0,
          |    isDeleted INTEGER NOT NULL DEFAULT 0,
          |    reminderDays INTEGER DEFAULT 0,
          |    createdAtEpochMs INTEGER NOT NULL,
          |    updatedAtEpochMs INTEGER NOT NULL
          |)
          """.trimMargin(), 0).await()
    }

    private fun migrateInternal(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
    ): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      if (oldVersion <= 1 && newVersion > 1) {
        driver.execute(null, """
            |CREATE TABLE PiggyBankEntity (
            |    id TEXT NOT NULL PRIMARY KEY,
            |    name TEXT NOT NULL,
            |    accumulatedCents INTEGER NOT NULL,
            |    emoji TEXT NOT NULL,
            |    colorHex TEXT NOT NULL,
            |    imageUri TEXT,
            |    isDeleted INTEGER NOT NULL DEFAULT 0,
            |    createdAtEpochMs INTEGER NOT NULL,
            |    updatedAtEpochMs INTEGER NOT NULL
            |)
            """.trimMargin(), 0).await()
      }
    }

    override fun migrate(
      driver: SqlDriver,
      oldVersion: Long,
      newVersion: Long,
      vararg callbacks: AfterVersion,
    ): QueryResult.AsyncValue<Unit> = QueryResult.AsyncValue {
      var lastVersion = oldVersion

      callbacks.filter { it.afterVersion in oldVersion until newVersion }
      .sortedBy { it.afterVersion }
      .forEach { callback ->
        migrateInternal(driver, oldVersion = lastVersion, newVersion = callback.afterVersion +
          1).await()
        callback.block(driver)
        lastVersion = callback.afterVersion + 1
      }

      if (lastVersion < newVersion) {
        migrateInternal(driver, lastVersion, newVersion).await()
      }
    }
  }
}
